import React, { useMemo, useState, useEffect } from 'react';
import {
  Grid,
  Container,
  Box,
  Typography,
  Card,
  Stack,
  Button,
  IconButton,
  Divider,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { useSelector } from 'react-redux';
import { useSettingsContext } from '../../../components/settings';
import {
  BookingDetails,
  BookingNewestBooking,
} from '../../../sections/@dashboard/general/booking';
import { useProductTranslation } from '../../../hooks/useProductTranslation';

import { ExtractedItem, CompletedTrioItem } from '../types/voiceOrderingTypes';

export interface VoiceOrderingLayoutProps {
  products: any[];
  extractedData: { items: ExtractedItem[] };
  trioData?: CompletedTrioItem[];
  onRemoveSolo?: (productId: string) => void;
  onRemoveTrio?: (productId: string) => void;
  voiceStatusComponent: React.ReactNode;
  isOrderActive?: boolean;
  isProcessingOrder?: boolean;
  isConfirmingOrder?: boolean;
  title?: string;
  showRecommendations?: boolean;
  showPromotionalImage?: boolean;
  isMinimal?: boolean;
  onToggleMinimal?: () => void;
  onResetOrder?: () => void;
  onCheckout?: () => void;
  useGemini?: boolean;
  onToggleGemini?: (enabled: boolean) => void;
}

export default function VoiceOrderingLayout({
  products,
  extractedData,
  trioData = [],
  onRemoveSolo,
  onRemoveTrio,
  voiceStatusComponent,
  isOrderActive = false,
  isProcessingOrder = false,
  isConfirmingOrder = false,
  title = "",
  showRecommendations = true,
  showPromotionalImage = true,
  isMinimal = false,
  onToggleMinimal,
  onResetOrder,
  onCheckout,
  useGemini = false,
  onToggleGemini,
}: VoiceOrderingLayoutProps) {
  const { themeStretch } = useSettingsContext();
  const { organization } = useSelector((state: any) => state.organization);
  const { getName, getDescription } = useProductTranslation(organization?.defaultLanguage);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const translatedProducts = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        name: getName(product.translations),
        description: getDescription(product.translations),
      })),
    [products, getName, getDescription]
  );

  const popularProducts = useMemo(() => translatedProducts.slice(0, 4), [translatedProducts]);

  // TODO: Refactor tax calculation into a shared utility in future sprints. 
  // This logic is currently duplicated from BookingDetails to support the new minimal design.
  // Tax Logic (copied from BookingDetails)
  const soloItemsSubtotal = extractedData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const trioItemsSubtotal = (trioData || []).reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  const grandSubtotal = soloItemsSubtotal + trioItemsSubtotal;

  const fedRate = organization?.federalTaxRate ?? 5.0;
  const provRate = organization?.provincialTaxRate ?? 9.975;

  const tps = grandSubtotal * (fedRate / 100);
  const tvq = grandSubtotal * (provRate / 100);
  const totalTax = tps + tvq;
  const grandTotal = grandSubtotal + totalTax;

  const fCurrency = (value: number) =>
    new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(value);

  if (!isMinimal) {
    return (
      <Container maxWidth={themeStretch ? false : 'xl'} sx={{ pb: 5 }}>
        <Grid spacing={3} container marginTop={2}>
          <Grid item xs={12}>
            {voiceStatusComponent}
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <BookingNewestBooking
              title="Les plus commandés"
              subheader=""
              list={translatedProducts}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 4 }}>
            <BookingDetails
              title="Votre commande"
              subheader=""
              cartData={extractedData.items}
              trioData={trioData}
              onRemoveSolo={onRemoveSolo}
              onRemoveTrio={onRemoveTrio}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  // --- NEW MINIMAL DESIGN ---
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'white',
        zIndex: 5000,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Inter', sans-serif",
        overflowY: { xs: 'auto', md: 'hidden' }, // Scrollable on mobile
      }}
    >
      {/* 1. Header */}
      <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={organization?.id ? `${process.env.REACT_APP_HOST_API_KEY}/api/v1/organizations/${organization.id}/image` : "/logo.png"}
            sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
            imgProps={{
              onError: (e: any) => { e.currentTarget.src = '/logo.png'; }
            }}
          >
            <MicIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1 }}>
              {organization?.name || 'AI-LYN'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
              Drive intelligent
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <WbSunnyIcon sx={{ color: '#F59E0B', fontSize: 18 }} />
          </Typography>

          {/* Gemini Switcher */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={useGemini}
                onChange={(e) => onToggleGemini?.(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3B82F6' }
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
                GEMINI LIVE (BETA)
              </Typography>
            }
            sx={{ m: 0 }}
          />

          <Button
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#1E293B',
              borderColor: '#E2E8F0',
              minWidth: { xs: 'auto', sm: 64 },
              px: { xs: 1, sm: 2 }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Aide ?</Box>
          </Button>
          {onToggleMinimal && (
            <IconButton onClick={onToggleMinimal} sx={{ color: '#64748B' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* 2. Main Body */}
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, overflow: { xs: 'visible', md: 'hidden' } }}>
        <Grid container spacing={{ xs: 2, md: 4 }} sx={{ height: { xs: 'auto', md: '100%' } }}>
          {/* Left Column */}
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: { xs: 'auto', md: '100%' }, overflowY: { xs: 'visible', md: 'auto' }, pr: { md: 1 } }}>
            {/* Listening Card */}
            <Card
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                position: 'relative',
                flexGrow: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '300px',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 100%)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'absolute', top: 30, left: 30 }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: isOrderActive ? '#3B82F6' : '#94A3B8',
                  animation: isOrderActive ? 'pulse-green 2s infinite' : 'none',
                  transition: 'background-color 0.2s ease',
                }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', letterSpacing: 1 }}>
                  {isOrderActive ? 'IA EN ÉCOUTE' : 'IA EN ATTENTE'}
                </Typography>
              </Stack>

              <Grid container alignItems="center" sx={{ mt: 4 }} spacing={2}>
                <Grid item xs={12} sm={7}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                    Je vous écoute
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#64748B', mb: 2 }}>
                    Dites <Box component="span" sx={{ color: '#2563EB', fontWeight: 700 }}>&quot;Commencer la commande&quot;</Box>
                  </Typography>

                  {/* Wave effect placeholder */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40, mb: 1 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <Box key={i} sx={{
                        width: 4,
                        height: isOrderActive ? (10 + Math.random() * 30) : 10,
                        bgcolor: '#3B82F6',
                        borderRadius: 2,
                        transition: 'height 0.2s ease-in-out',
                        opacity: 0.4 + (i / 10)
                      }} />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={5} sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 0 } }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 240,
                      height: 240,
                      borderRadius: '50%',
                      border: '1px solid #E2E8F0',
                      opacity: 0.5
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 180,
                      height: 180,
                      borderRadius: '50%',
                      border: '1px solid #E2E8F0'
                    }} />
                    <IconButton
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: '#2563EB',
                        color: 'white',
                        boxShadow: '0 10px 30px rgba(37, 99, 235, 0.3)',
                        '&:hover': { bgcolor: '#1D4ED8' },
                        animation: isOrderActive ? 'pulse-blue 2s infinite' : 'none',
                        '@keyframes pulse-blue': {
                          '0%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0.5)' },
                          '70%': { boxShadow: '0 0 0 20px rgba(37, 99, 235, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0)' },
                        },
                        '@keyframes pulse-green': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                          '100%': { opacity: 1 },
                        }
                      }}
                    >
                      <MicIcon sx={{ fontSize: 60 }} />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{
                mt: { xs: 4, sm: 2 },
                p: 2,
                bgcolor: '#EFF6FF',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                maxWidth: '400px',
                border: '1px solid #DBEAFE'
              }}>
                <LightbulbIcon sx={{ color: '#F59E0B' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E40AF' }}>
                  Essayez : <Box component="span" sx={{ color: '#2563EB' }}>&quot;Je veux un combo burger&quot;</Box>
                </Typography>
              </Box>
            </Card>

            {/* Popular Products */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <WhatshotIcon sx={{ color: '#EF4444' }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                  Populaires
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {popularProducts.map((product) => (
                  <Grid item xs={6} sm={4} md={3} key={product.id}>
                    <Card sx={{
                      borderRadius: 3,
                      textAlign: 'center',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.02)',
                      border: '1px solid #F1F5F9',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ height: 120, width: '100%', overflow: 'hidden', bgcolor: '#F8FAFC' }}>
                        <Box
                          component="img"
                          src={`${process.env.REACT_APP_HOST_API_KEY}/api/v1/products/${product.id}/image`}
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.style.background = 'linear-gradient(135deg, #e2e8f0, #f1f5f9)';
                          }}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>

                      <Box sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 700,
                          color: '#1E293B',
                          mb: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.2,
                          fontSize: '0.8rem'
                        }}>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.85rem' }}>
                          {fCurrency(product.price)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Right Column - Cart */}
          <Grid item xs={12} md={4} sx={{ height: { xs: 'auto', md: '100%' }, minHeight: { xs: '500px', md: 'auto' } }}>
            <Card sx={{
              height: '100%',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              border: '1px solid #F1F5F9'
            }}>
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ShoppingBasketIcon sx={{ color: '#2563EB' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                    Votre commande
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  color="error"
                  onClick={onResetOrder}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' } }}
                >
                  Effacer
                </Button>
              </Box>

              <Divider />

              {/* Items List */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
                <Stack spacing={3}>
                  {extractedData.items.map((item, idx) => {
                    const allOptions = [
                      ...item.addOns,
                      ...(item.selections || []).map((s: any) => s.name)
                    ].filter(Boolean);

                    return (
                      <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {item.productName}
                          </Typography>
                          {allOptions.length > 0 && (
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                              {allOptions.join(' | ')}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {item.quantity}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B', minWidth: 60, textAlign: 'right' }}>
                            {fCurrency(item.price * item.quantity)}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                  {(trioData || []).map((item, idx) => {
                    const trioOptions = Object.values(item.selections || {})
                      .flat()
                      .map((s: any) => s.name)
                      .filter(Boolean);

                    return (
                      <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {item.productName} <Box component="span" sx={{ color: '#2563EB', fontSize: '0.8rem' }}>(Trio)</Box>
                          </Typography>
                          {trioOptions.length > 0 && (
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                              {trioOptions.join(' | ')}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {item.quantity}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B', minWidth: 60, textAlign: 'right' }}>
                            {fCurrency(item.totalPrice)}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                  {extractedData.items.length === 0 && (trioData || []).length === 0 && (
                    <Box sx={{ py: 10, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        Aucun article pour le moment
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Box sx={{ p: 3, bgcolor: '#F8FAFC' }}>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Sous-total</Typography>
                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 700 }}>{fCurrency(grandSubtotal)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Taxes ({fedRate + provRate}%)</Typography>
                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 700 }}>{fCurrency(totalTax)}</Typography>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>TOTAL</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#2563EB' }}>{fCurrency(grandTotal)}</Typography>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    width: '100%',
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    color: 'white',
                    bgcolor: isConfirmingOrder ? '#22C55E' : '#2563EB',
                    boxShadow: isConfirmingOrder ? '0 4px 14px rgba(34, 197, 94, 0.4)' : '0 4px 14px rgba(37, 99, 235, 0.4)',
                    transition: 'all 0.3s'
                  }}
                >
                  <CheckCircleIcon />
                  {(() => {
                    if (isConfirmingOrder) return 'Commande envoyée !';
                    if (isProcessingOrder) return 'Analyse en cours...';
                    return 'Terminer la commande';
                  })()}
                </Box>

                <Box sx={{ mt: 2, p: 2, bgcolor: '#EFF6FF', borderRadius: 2, display: 'flex', gap: 1.5 }}>
                  <InfoOutlinedIcon sx={{ color: '#2563EB', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E40AF', display: 'block' }}>
                      Paiement à l&apos;étape suivante
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Vous pourrez vérifier et payer au prochain guichet.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* 3. Footer Bar */}
      <Box sx={{ px: 4, pb: 2, pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{
          width: '100%',
          bgcolor: '#0F172A',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 4,
          p: 3,
          mb: 2,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 4, md: 6 }} // Reduce spacing on mobile
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-start' } }}
          >
            {/* Step 1: En écoute */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: isOrderActive ? 1 : 0.5, transition: 'opacity 0.3s' }}>
              <Avatar sx={{
                width: { xs: 32, sm: 40 }, // Smaller icons on mobile
                height: { xs: 32, sm: 40 },
                bgcolor: 'transparent',
                border: isOrderActive ? '2px solid #2563EB' : '1px solid #334155',
                color: isOrderActive ? '#2563EB' : '#94A3B8',
                fontSize: { xs: 16, sm: 20 },
                animation: isOrderActive ? 'pulse-border 2s infinite' : 'none',
                '@keyframes pulse-border': {
                  '0%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0.4)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(37, 99, 235, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0)' },
                }
              }}>
                <MicIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 700, color: isOrderActive ? '#3B82F6' : '#94A3B8', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {/* Short text for mobile */}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>En écoute</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Écoute</Box>
              </Typography>
            </Stack>

            {/* Dots - Hidden on mobile to save space */}
            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: isOrderActive ? '#2563EB' : '#1E293B', opacity: isOrderActive ? 0.5 : 1 }} />
              ))}
            </Stack>

            {/* Step 2: Commande */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: (extractedData.items.length > 0 || trioData.length > 0) ? 1 : 0.5, transition: 'opacity 0.3s' }}>
              <Avatar sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                bgcolor: 'transparent',
                border: (extractedData.items.length > 0 || trioData.length > 0) ? '2px solid #22C55E' : '1px solid #334155',
                color: (extractedData.items.length > 0 || trioData.length > 0) ? '#22C55E' : '#94A3B8',
                fontSize: { xs: 16, sm: 20 }
              }}>
                <ShoppingBasketIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 700, color: (extractedData.items.length > 0 || trioData.length > 0) ? '#22C55E' : '#94A3B8', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                Commande
              </Typography>
            </Stack>

            {/* Dots - Hidden on mobile */}
            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: (extractedData.items.length > 0 || trioData.length > 0) ? '#22C55E' : '#1E293B', opacity: (extractedData.items.length > 0 || trioData.length > 0) ? 0.5 : 1 }} />
              ))}
            </Stack>

            {/* Step 3: Confirmation */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: isConfirmingOrder ? 1 : 0.5, transition: 'opacity 0.3s' }}>
              <Avatar sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                bgcolor: 'transparent',
                border: isConfirmingOrder ? '2px solid #F59E0B' : '1px solid #334155',
                color: isConfirmingOrder ? '#F59E0B' : '#94A3B8',
                fontSize: { xs: 16, sm: 20 },
                animation: isConfirmingOrder ? 'pulse-border-yellow 2s infinite' : 'none',
                '@keyframes pulse-border-yellow': {
                  '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(245, 158, 11, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
                }
              }}>
                <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 700, color: isConfirmingOrder ? '#F59E0B' : '#94A3B8', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Confirmation</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Confirm.</Box>
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={4} alignItems="center" sx={{ display: { xs: 'none', lg: 'flex' } }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'white', display: 'block' }}>Conseil vocal</Typography>
              <Typography variant="body2" sx={{ color: '#94A3B8' }}>Pour un service plus rapide, soyez précis dans votre commande.</Typography>
            </Box>
          </Stack>
        </Card>

        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>
          <Box component="span" sx={{ color: '#2563EB' }}>{organization?.name || 'AI-LYN'}</Box> Drive intelligent &bull; Commandez avec votre voix
        </Typography>
      </Box>

    </Box>
  );
}
